import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import axios from 'axios';
import { Field, Form, Formik } from 'formik';
import type { FC } from 'react';
import React from 'react';
import { BiInfoCircle } from 'react-icons/bi';
import * as Yup from 'yup';

import { strapiUrl } from '../config/env';
import { daCopy } from '../content/copy/da';

type InterestFormMode = 'inline' | 'modal';

interface InterestFormProps {
  mode?: InterestFormMode;
  onSubmitted?: () => void;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required(daCopy.form.errors.nameRequired),
  email: Yup.string()
    .email(daCopy.form.errors.emailInvalid)
    .required(daCopy.form.errors.emailRequired),
  phone: Yup.string()
    .matches(/^(\+45)?\s?(\d{2}\s?){3}\d{2}$/, daCopy.form.errors.phoneInvalid)
    .required(daCopy.form.errors.phoneRequired),
  rooms: Yup.number()
    .typeError(daCopy.form.errors.roomsRequired)
    .required(daCopy.form.errors.roomsRequired),
  rent: Yup.number()
    .typeError(daCopy.form.errors.rentInvalid)
    .required(daCopy.form.errors.rentRequired),
  interest: Yup.date()
    .typeError(daCopy.form.errors.interestInvalid)
    .required(daCopy.form.errors.interestRequired),
});

const initialValues = {
  name: '',
  email: '',
  phone: '',
  rooms: '',
  rent: '',
  interest: '',
};

const FormMessage: FC<{ text: string }> = ({ text }) => (
  <div className="form-success is-visible" role="status">
    {text}
  </div>
);

export const ApartmentInterestForm: FC<InterestFormProps> = ({
  mode = 'inline',
  onSubmitted,
}) => {
  const [hasSubmitCompleted, setHasSubmitCompleted] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className={`editorial-form${mode === 'modal' ? ' editorial-form--modal' : ''}`}>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={async (values, actions) => {
          try {
            setSubmitError('');

            await axios.post(`${strapiUrl}/mulige-lejeres`, {
              Navn: values.name,
              Telefon: values.phone,
              Email: values.email,
              Max_Husleje: values.rent,
              Antal_Rum: values.rooms,
              Interesseret_indtil: values.interest,
            });

            setHasSubmitCompleted(true);
            onSubmitted?.();
            actions.resetForm();
          } catch (error: any) {
            setSubmitError(error?.message || daCopy.form.errors.genericSubmit);
          } finally {
            actions.setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form className="form-grid">
            {hasSubmitCompleted ? <FormMessage text={daCopy.form.success} /> : null}
            {submitError ? <div className="form-error">{daCopy.form.errors.genericSubmit}</div> : null}

            <div className="form-grid form-grid--two">
              <Field name="name">
                {({ field }: { field: any }) => (
                  <div className="form-field">
                    <label htmlFor={`${mode}-name`}>{daCopy.form.labels.name}</label>
                    <input
                      {...field}
                      id={`${mode}-name`}
                      autoComplete="name"
                      placeholder={daCopy.form.placeholders.name}
                    />
                    {errors.name && touched.name ? <div className="form-error">{errors.name}</div> : null}
                  </div>
                )}
              </Field>

              <Field name="phone">
                {({ field }: { field: any }) => (
                  <div className="form-field">
                    <label htmlFor={`${mode}-phone`}>{daCopy.form.labels.phone}</label>
                    <input
                      {...field}
                      id={`${mode}-phone`}
                      autoComplete="tel-national"
                      inputMode="tel"
                      placeholder={daCopy.form.placeholders.phone}
                      type="tel"
                    />
                    {errors.phone && touched.phone ? <div className="form-error">{errors.phone}</div> : null}
                  </div>
                )}
              </Field>
            </div>

            <Field name="email">
              {({ field }: { field: any }) => (
                <div className="form-field">
                  <label htmlFor={`${mode}-email`}>{daCopy.form.labels.email}</label>
                  <input
                    {...field}
                    id={`${mode}-email`}
                    autoComplete="email"
                    placeholder={daCopy.form.placeholders.email}
                    type="email"
                  />
                  {errors.email && touched.email ? <div className="form-error">{errors.email}</div> : null}
                </div>
              )}
            </Field>

            <div className="form-grid form-grid--two">
              <Field name="rooms">
                {({ field }: { field: any }) => (
                  <div className="form-field">
                    <label htmlFor={`${mode}-rooms`}>{daCopy.form.labels.rooms}</label>
                    <select {...field} id={`${mode}-rooms`}>
                      <option value="">{daCopy.form.placeholders.rooms}</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                    </select>
                    {errors.rooms && touched.rooms ? <div className="form-error">{errors.rooms}</div> : null}
                  </div>
                )}
              </Field>

              <Field name="rent">
                {({ field }: { field: any }) => (
                  <div className="form-field">
                    <label htmlFor={`${mode}-rent`}>{daCopy.form.labels.rent}</label>
                    <input
                      {...field}
                      id={`${mode}-rent`}
                      inputMode="numeric"
                      min={1}
                      placeholder={daCopy.form.placeholders.rent}
                      type="number"
                    />
                    {errors.rent && touched.rent ? <div className="form-error">{errors.rent}</div> : null}
                  </div>
                )}
              </Field>
            </div>

            <Field name="interest">
              {({ field }: { field: any }) => (
                <div className="form-field">
                  <label htmlFor={`${mode}-interest`}>{daCopy.form.labels.interest}</label>
                  <input {...field} id={`${mode}-interest`} min={today} type="date" />
                  <div className="form-hint">Vi kontakter dig ikke efter denne dato.</div>
                  {errors.interest && touched.interest ? <div className="form-error">{errors.interest}</div> : null}
                </div>
              )}
            </Field>

            <button className="site-btn site-btn--primary" disabled={isSubmitting} type="submit">
              {isSubmitting ? 'Sender…' : daCopy.form.submit}
            </button>
          </Form>
        )}
      </Formik>

      {mode === 'inline' ? (
        <div className="form-note">
          <BiInfoCircle size={18} />
          <span>
            Dine oplysninger behandles fortroligt og bruges kun til at kontakte dig om ledige lejemål.
          </span>
        </div>
      ) : null}
    </div>
  );
};

export const SignUpForApartment = ({
  isOpen,
  onOpen,
  onClose,
}: {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.500" />
      <ModalContent className="policy-modal">
        <ModalHeader>{daCopy.cta.signUp}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text color="var(--muted)" mb={6}>
            Fortæl os lidt om dine boligønsker, så tager vi fat i dig, når vi har et relevant lejemål.
          </Text>
          <ApartmentInterestForm mode="modal" onSubmitted={onClose} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
